import { Injectable, inject } from '@angular/core';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { PlatformChartExportItemDto } from '@data/_generated-api/platform-api/models/platform-chart-export-item-dto';
import { PlatformChartService } from '@data/_generated-api/platform-api/services/platform-chart.service';
import { downloadText } from '@shared/utils/download-file.utils';
import { ChartExportFormat, ChartExportRecord, toChartExportRecord } from './chart-export.models';
import { buildChartExportFile } from './chart-export-file';

/** Uitkomst van een export; `skipped` = charts die niet meegingen (geen standaard-chart of zonder geboortemoment). */
export interface ChartExportOutcome {
  readonly exported: number;
  readonly skipped: number;
}

/** Spiegel van GetChartsExportValidator.MaxChartIds in de backend. */
const EXPORT_MAX_IDS_PER_REQUEST = 5000;

/**
 * Chart-export uit de chart manager (docs/human-design/hd-chart-export.md): haalt de
 * geboortegegevens van de gevraagde charts op (enkel eigen standaard-charts, de backend filtert),
 * bouwt het bestand in het gekozen formaat en laat de browser het downloaden.
 */
@Injectable({ providedIn: 'root' })
export class HdChartExportService {
  private readonly chartApi = inject(PlatformChartService);

  async exportCharts(chartIds: readonly string[], format: ChartExportFormat): Promise<ChartExportOutcome> {
    const uniqueIds = [...new Set(chartIds)];
    if (uniqueIds.length === 0) return { exported: 0, skipped: 0 };

    const records = (await this.loadItems(uniqueIds))
      .map(toChartExportRecord)
      .filter((record): record is ChartExportRecord => !!record);
    const skipped = uniqueIds.length - records.length;
    if (records.length === 0) return { exported: 0, skipped };

    const file = buildChartExportFile(records, format, moment.utc());
    downloadText(file.content, file.fileName, file.mimeType);
    return { exported: records.length, skipped };
  }

  private async loadItems(chartIds: readonly string[]): Promise<PlatformChartExportItemDto[]> {
    const items: PlatformChartExportItemDto[] = [];
    for (let start = 0; start < chartIds.length; start += EXPORT_MAX_IDS_PER_REQUEST) {
      const result = await firstValueFrom(
        this.chartApi.postChartsQueryExport({ body: { chartIds: chartIds.slice(start, start + EXPORT_MAX_IDS_PER_REQUEST) } }),
      );
      if (!result?.isValid) throw new Error(result?.simpleMessage || 'De export is mislukt.');
      items.push(...(result.value ?? []));
    }
    return items;
  }
}
